import { useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArchiveIcon from '@mui/icons-material/Archive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, Box, Typography, FormControl, Select, InputLabel, MenuItem, TextField, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useMergeQuery } from '../../features/merge/mergeHooks';
import { useAppDispatch } from '../../app/hooks';
import { useCreateActivity } from '../../features/activity/activityHooks';
import { selectProject, setMergeTemplate } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { selectMerges, setMerges, setMerge, selectCount } from '../../features/merge/mergeSlice';
import { showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { IMerge } from '../../types/merge';
import { IMergeTemplates } from '../../types/project';
import StandardTable from '../tables/StandardTable';
import CustomDialog from '../common/CustomDialog';
import { FlexBetween } from '../../commonStyles';
import { mergeType } from '../../utils/Constants';
import { Flex } from '../../commonStyles';
import env from '../../config/env';

const CreateMerges = () => {
  const storeDispatch = useAppDispatch();
  const createActivity = useCreateActivity();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const merges = useSelector(selectMerges);
  const count = useSelector(selectCount);
  const [selectedMergeType, setSelectedMergeType] = useState<string>('APS');
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [dialog, setDialog] = useState<{ open: boolean; type: string; merge?: IMerge }>({ open: false, type: '' });
  // While searching by name, don't constrain results to the selected Merge Template Type —
  // search across every type instead. The type filter still applies once the search is cleared.
  const { loading } = useMergeQuery(project._id, search ? mergeType : [selectedMergeType], pageNumber, 11, search);

  const [deleteMergeTemplate] = useMutation(DELETEMERGETEMPLATE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`${data.mergeTemplateDeleteById.name} has been deleted!`));
      let filteredMerge = merges.filter((merge: IMerge, index: number) => merge._id !== data.mergeTemplateDeleteById._id);
      storeDispatch(setMerges({ items: filteredMerge, count: filteredMerge.length }));
      createActivity({
        project: project._id,
        user: user._id,
        deal: null,
        title: `Delete Template`,
        content: `Template ${data.mergeTemplateDeleteById.name} has been deleted`,
      });
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [copyMerge] = useMutation(COPYMERGE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`${data.copyMergeTemplate.name} has been created`));
      storeDispatch(setMerges({ items: [...merges, data.copyMergeTemplate], count: [...merges, data.copyMergeTemplate].length }));

      if (data.copyMergeTemplate.type === 'APS') {
        storeDispatch(setMergeTemplate(data.copyMergeTemplate.project.mergeTemplates));
      }
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [archiveMerge] = useMutation(UPDATEMERGETEMPLATE, {
    onCompleted: (data) => {
      let filteredMerges = merges.filter((merge: IMerge) => merge._id !== data.mergeTemplateUpdateById.record._id);
      storeDispatch(showSuccessSnackbar('Merge Template has been archived!'));
      storeDispatch(setMerges({ items: filteredMerges, count: filteredMerges.length }));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const setNewMerge = () => {
    storeDispatch(
      setMerge({
        _id: 0,
        projectId: 0,
        name: '',
        mergeFields: [],
        signFields: [],
        type: '',
        default: false,
      })
    );
  };

  const handleDelete = (id: string, name: string) => {
    let filteredMerge = project.mergeTemplates.filter((merge: IMergeTemplates, index: number) => merge.mergeTemplate?._id !== id);
    deleteMergeTemplate({ variables: { _id: id, deleteFile: true } });
    let selectedMerge = merges.find((merge: IMerge) => {
      return name === merge.name;
    });
    if (selectedMerge?.type === 'APS') {
      let filteredAPSTemplate = filteredMerge.map((filtered: IMergeTemplates) => {
        let apsTemplate = filtered.apsTemplates.filter((aps: any) => name !== aps.name);
        return {
          ...filtered,
          apsTemplates: apsTemplate,
        };
      });
      storeDispatch(setMergeTemplate(filteredAPSTemplate));
    } else {
      storeDispatch(setMergeTemplate(filteredMerge));
    }
  };

  const handleCopy = (id: string, title: string) => {
    copyMerge({ variables: { _id: id } });
  };

  const handleArchive = (id: string, title: string) => {
    archiveMerge({ variables: { _id: id, record: { type: 'Archive' } } });
  };

  const openDialog = (type: string, merge: IMerge) => setDialog({ open: true, type, merge });
  const closeDialog = () => setDialog({ open: false, type: '' });

  const handleDialogConfirm = () => {
    const merge = dialog.merge;
    if (merge?._id) {
      if (dialog.type === 'delete') handleDelete(merge._id, merge.name);
      else if (dialog.type === 'copy') handleCopy(merge._id, merge.name);
      else if (dialog.type === 'archive') handleArchive(merge._id, merge.name);
    }
    closeDialog();
  };

  const dialogTitle = () => {
    if (dialog.type === 'delete') return 'Delete?';
    if (dialog.type === 'copy') return 'Copy?';
    if (dialog.type === 'archive') return `Archive ${dialog.merge?.name}?`;
    return '';
  };

  const dialogContent = () => {
    if (dialog.type === 'delete') {
      return (
        <Box sx={{ mb: 2 }}>
          Are you sure you would like to delete <strong>{dialog.merge?.name}</strong>?
        </Box>
      );
    }
    if (dialog.type === 'copy') {
      return (
        <Box sx={{ mb: 2 }}>
          Are you sure you would like to copy <strong>{dialog.merge?.name}</strong>?
        </Box>
      );
    }
    if (dialog.type === 'archive') {
      return (
        <Box sx={{ mb: 2 }}>
          Are you sure you would like to archive <strong>{dialog.merge?.name}</strong>?
        </Box>
      );
    }
    return null;
  };

  const columns = [
    {
      Header: 'Name',
      accessor: (merge: IMerge) => (
        <Link
          style={{ color: '#2e453d', textDecoration: 'none', fontWeight: 700 }}
          to={`/${project._id}/dashboard/editmerges/${merge._id}`}
          onClick={() => setSearch('')}
        >
          {merge.name}
        </Link>
      ),
    },
    {
      Header: 'Type',
      accessor: (merge: IMerge) => merge.type,
    },
    {
      Header: 'Total Pages',
      accessor: (merge: IMerge) => merge.totalPages,
    },
    {
      Header: 'Actions',
      accessor: (merge: IMerge) => (
        <Flex>
          {merge.type !== 'Archive' ? (
            <Tooltip title="Archive">
              <ArchiveIcon sx={{ cursor: 'pointer', mr: 1 }} onClick={() => openDialog('archive', merge)} />
            </Tooltip>
          ) : null}
          <a
            rel="noreferrer"
            target="_blank"
            style={{ textDecoration: 'none', color: 'inherit' }}
            href={`${env.basePath}/pdfs/merge-templates/${merge._id}`}
          >
            <Tooltip title="Download">
              <DownloadIcon sx={{ cursor: 'pointer', mr: 1 }} />
            </Tooltip>
          </a>
          <Tooltip title="Copy">
            <ContentCopyIcon sx={{ cursor: 'pointer', mr: 1 }} onClick={() => openDialog('copy', merge)} />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteForeverIcon sx={{ cursor: 'pointer' }} onClick={() => openDialog('delete', merge)} />
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ mb: 2 }} variant={'h5'}>
        <strong>Merges</strong>
      </Typography>
      <FlexBetween sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Flex sx={{ gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ width: '200px', alignSelf: 'center' }}>
            <InputLabel id="merge-template-search">Merge Template Type</InputLabel>
            <Select
              size="small"
              labelId="merge-template-search"
              value={selectedMergeType}
              label="Merge Template Type"
              onChange={(e) => {
                setPageNumber(0);
                setSelectedMergeType(e.target.value);
              }}
            >
              {mergeType.map((value: string, index: number) => {
                return (
                  <MenuItem key={index} value={value}>
                    {value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: '300px', alignSelf: 'center' }}>
            <TextField
              size="small"
              title={'Search By Name'}
              name={'search'}
              fullWidth
              value={search}
              label={'Search By Name'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPageNumber(0);
                setSearch(e.target.value);
              }}
            />
          </FormControl>
        </Flex>
        <Flex sx={{ gap: 1 }}>
          <Link
            style={{
              color: '#2e453d',
              textDecoration: 'none',
            }}
            state={{
              floorplanLink: false,
            }}
            to={`/${project._id}/dashboard/create-merges/new`}
          >
            <Button onClick={() => setNewMerge()} variant="contained" color="primary" startIcon={<AddCircleOutlineIcon />}>
              Add A Merge
            </Button>
          </Link>
          <Link
            style={{
              color: '#2e453d',
              textDecoration: 'none',
            }}
            state={{
              floorplanLink: true,
            }}
            to={`/${project._id}/dashboard/editmerges/floorplans`}
          >
            <Button onClick={() => setNewMerge()} variant="contained" color="primary">
              Floorplan Merges
            </Button>
          </Link>
          <Link
            style={{
              color: '#2e453d',
              textDecoration: 'none',
            }}
            state={{
              floorplanLink: true,
            }}
            to={`/${project._id}/dashboard/editmerges/warranties`}
          >
            <Button onClick={() => setNewMerge()} variant="contained" color="primary">
              Warranty Merges
            </Button>
          </Link>
        </Flex>
      </FlexBetween>
      <StandardTable columns={columns} data={merges || []} loading={loading} text="No merge templates found" />
      <CustomDialog
        open={dialog.open}
        handleClose={closeDialog}
        handleCloseRemove={closeDialog}
        handleCloseSuccess={handleDialogConfirm}
        removeButton="No"
        successButton="Yes"
        dialogTitle={dialogTitle()}
        dialogContent={dialogContent()}
      />
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button disabled={!pageNumber} onClick={() => setPageNumber(pageNumber - 1)}>
          {'<'}
        </Button>
        <span>{pageNumber + 1}</span>
        <Button disabled={pageNumber + 1 === Math.ceil(count / 11)} onClick={() => setPageNumber(pageNumber + 1)}>
          {'>'}
        </Button>
      </Box>
    </Box>
  );
};

const UPDATEMERGETEMPLATE = gql`
  mutation mergeTemplateUpdateById($_id: MongoID!, $record: UpdateByIdMergeTemplateInput!) {
    mergeTemplateUpdateById(_id: $_id, record: $record) {
      record {
        _id
      }
    }
  }
`;

const DELETEMERGETEMPLATE = gql`
  mutation mergeTemplateDeleteById($_id: MongoID!, $deleteFile: Boolean!) {
    mergeTemplateDeleteById(_id: $_id, deleteFile: $deleteFile) {
      _id
      name
    }
  }
`;

const COPYMERGE = gql`
  mutation copyMergeTemplate($_id: MongoID!) {
    copyMergeTemplate(_id: $_id) {
      _id
      project {
        _id
        mergeTemplates {
          name
          mergeTemplate {
            name
            _id
            totalPages
          }
          apsTemplates {
            name
            apsTemplate {
              type
              _id
              name
            }
            pageNumber
            attachToAps
          }
        }
      }
      name
      totalPages
      type
    }
  }
`;

export default CreateMerges;
